import React, { useState, useEffect } from 'react';
import { FaUser, FaEnvelope, FaSave, FaUserEdit, FaPhone, FaMapMarkerAlt, FaGraduationCap, FaHospital, FaUserMd } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import SearchableDropdown from './SearchableDropdown';
import '../styles/SearchableDropdown.css';
import '../styles/ModifyUser.css';
import axiosInstance from '../utils/axiosConfig';
import { getApiUrl } from '../utils/config';
import { parseNameFromFullName } from '../utils/userUtils';
import { getPreceptorRankIds } from '../utils/rankUtils';
import LoadingSpinner from './LoadingSpinner';
import { useToast } from '../utils/toastContext';
import UserDocuments from './UserDocuments';
import RankMultiSelect from './RankMultiSelect';
import RankSelect from './RankSelect';

const ModifyUser = ({ userId, userType, isAdmin = false }) => {
    const location = useLocation();
    const { showError, showSuccess } = useToast();
    // userType prop determines which fields are shown:
    // - 'student': Only basic info + rank + studyType (no speciality/institution fields)
    // - 'preceptor': Basic info + rank + speciality + institution + admin privileges
    // - 'admin': Only basic info (no speciality/institution/student fields)
    const [userData, setUserData] = useState({
        full_name: '',
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        address: '',
        id_number: '',
        id_expiry_date: '',
        iban: '',
        nationality: '',
        // Student specific
        rank: '',
        studyType: '',
        // Preceptor specific
        rankIds: [],
        specialityId: '',
        institutionId: '',
        specialityIds: [],
        institutionIds: [],
        subSpeciality: '',
        subInstitution: '',
        isAdmin: false,
        // Admin specific (no additional fields needed beyond basic info)
    });
    const [fieldErrors, setFieldErrors] = useState({ id_number: '', id_expiry_date: '', iban: '' });
    const [specialities, setSpecialities] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const [ranks, setRanks] = useState([]);
    const [subSpecialities, setSubSpecialities] = useState([]);
    const [subInstitutions, setSubInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const isPreceptorSelfAccount = userType === 'preceptor'
        && location.pathname.toLowerCase().includes('/preceptordashboard')
        && location.pathname.toLowerCase().includes('my-account');
    const canEditAdminPrivileges = isAdmin && !isPreceptorSelfAccount;

    useEffect(() => {
        fetchUserData();
        // Only fetch specialities and institutions if this is a preceptor
        if (userType === 'preceptor') {
            fetchSpecialities();
            fetchInstitutions();
            fetchRanks();
        }
    }, [userId, userType]);

    useEffect(() => {
        if (error) {
            showError(error);
        }
    }, [error, showError]);

    useEffect(() => {
        if (success) {
            showSuccess(success);
        }
    }, [success, showSuccess]);

    // Effect to fetch specialities and institutions when userData changes and user type is detected as preceptor
    useEffect(() => {
        // Only run this effect if userData has meaningful data (not just initial empty state)
        if ((userData.full_name || userData.firstname) && getDetectedUserType() === 'preceptor' && !specialities.length && !institutions.length) {
            fetchSpecialities();
            fetchInstitutions();
            fetchRanks();
        }
    }, [userData, specialities.length, institutions.length]);

    // Pre-migration preceptors have no rankIds list yet — once ranks load, resolve
    // their legacy `rank` string (including "both") into the equivalent id list.
    useEffect(() => {
        const currentRankIds = userData.rankIds || [];
        if (getDetectedUserType() !== 'preceptor' || !ranks.length || currentRankIds.length > 0 || !userData.rank) {
            return;
        }
        const resolved = getPreceptorRankIds({ rank: userData.rank }, ranks);
        if (resolved.length > 0) {
            setUserData((prev) => ({ ...prev, rankIds: resolved }));
        }
    }, [ranks, userData.rank, userData.rankIds]);



    // Auto-detect user type for UI rendering
    const getDetectedUserType = () => {
        if (userType) {
            // Handle preceptor-admin case - treat as preceptor with admin privileges
            if (userType === 'preceptor-admin') {
                return 'preceptor';
            }
            return userType; // Use prop if provided
        }
        
        // Auto-detect from userData
        if (userData.rank && ['spep', 'pharmd', 'alumni'].includes(userData.rank.toLowerCase())) {
            return 'student';
        } else if ((userData.specialityIds && userData.specialityIds.length > 0) || (userData.institutionIds && userData.institutionIds.length > 0) || userData.specialityId || userData.institutionId) {
            return 'preceptor';
        } else if (userData.isAdmin) {
            return 'admin';
        }
        return 'admin'; // Default fallback
    };

    // get_full_specialities returns [{ _id, name, totalAvailability, groups }] — use name for options
    const fetchSpecialities = async () => {
        try {
            const response = await axiosInstance.get('/crud/get_full_specialities');
            const data = Array.isArray(response.data) ? response.data : [];
            // Normalize specialities: ensure each has a 'name' field (fallback to 'speciality' if needed)
            const normalized = data.map(s => ({
                ...s,
                name: s.name || s.speciality || 'Unnamed'
            }));
            setSpecialities(normalized);
        } catch (error) {
            console.error('Error fetching specialities:', error);
        }
    };

    // get_full_institutions returns [{ _id, name, ... }] — use name for options
    const fetchInstitutions = async () => {
        try {
            const response = await axiosInstance.get('/crud/get_full_institutions');
            const data = Array.isArray(response.data) ? response.data : [];
            // Normalize institutions: ensure each has a 'name' field (fallback to 'institution' if needed)
            const normalized = data.map(i => ({
                ...i,
                name: i.name || i.institution || 'Unnamed'
            }));
            setInstitutions(normalized);
        } catch (error) {
            console.error('Error fetching institutions:', error);
        }
    };

    const fetchRanks = async () => {
        try {
            const response = await axiosInstance.get('/ranks/ranks');
            setRanks(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching ranks:', error);
        }
    };

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('access_token');
            
            // Check if userId is provided
            if (!userId) {
                throw new Error('User ID is required but not provided');
            }
            
            // Use the unified endpoint that works regardless of user role
            const endpoint = getApiUrl(`auth/get_user_info/${userId}`);

            const response = await fetch(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch user data');
            }

            const data = await response.json();
            
            // Extract user data from the nested user_info object
            const userData = data.user_info || data;
            
            // Auto-detect user type if not explicitly provided
            let detectedUserType = userType;
            if (!detectedUserType) {
                if (userData.rank && ['spep', 'pharmd', 'alumni'].includes(userData.rank.toLowerCase())) {
                    detectedUserType = 'student';
                } else if ((userData.specialityIds && userData.specialityIds.length > 0) || (userData.institutionIds && userData.institutionIds.length > 0) || userData.specialityId || userData.institutionId) {
                    detectedUserType = 'preceptor';
                } else if (userData.isAdmin) {
                    detectedUserType = 'admin';
                } else {
                    detectedUserType = 'admin'; // Default fallback
                }
            }
            
            // Handle preceptor-admin case - treat as preceptor with admin privileges
            if (detectedUserType === 'preceptor-admin') {
                detectedUserType = 'preceptor';
            }
            
            // firstname/lastname are the source of truth; full_name is only parsed out for
            // legacy records that never had separate firstname/lastname stored.
            const fullName = (userData.full_name ?? userData.fullName ?? '').trim() ||
                [userData.firstname, userData.lastname].filter(Boolean).join(' ').trim();
            const { firstname: parsedFirst, lastname: parsedLast } = parseNameFromFullName(fullName);
            const firstname = (userData.firstname != null && String(userData.firstname).trim() !== '') ? String(userData.firstname).trim() : parsedFirst;
            const lastname = (userData.lastname != null && String(userData.lastname).trim() !== '') ? String(userData.lastname).trim() : parsedLast;

            const baseData = {
                full_name: fullName || `${firstname} ${lastname}`.trim(),
                firstname,
                lastname,
                email: userData.email || '',
                phone: userData.phone || '',
                address: userData.address || '',
                id_number: (userData.id_number ?? userData.idNumber ?? '').toString().trim(),
                id_expiry_date: (userData.id_expiry_date ?? userData.idExpiryDate ?? '').toString().trim(),
                iban: (userData.iban ?? '').toString().trim(),
                nationality: (userData.nationality ?? '').toString().trim(),
            };

            if (detectedUserType === 'student') {
                // Only set student-specific fields
                setUserData({
                    ...baseData,
                    rank: userData.rank || '',
                    studyType: userData.studyType || '',
                    // Reset preceptor fields for students
                    rankIds: [],
                    specialityId: '',
                    institutionId: '',
                    specialityIds: [],
                    institutionIds: [],
                    subSpeciality: '',
                    subInstitution: '',
                    isAdmin: false,
                });
            } else if (detectedUserType === 'preceptor') {
                const specialityIds = Array.isArray(userData.specialityIds) && userData.specialityIds.length > 0
                    ? userData.specialityIds
                    : (userData.specialityId ? [userData.specialityId] : []);
                const institutionIds = Array.isArray(userData.institutionIds) && userData.institutionIds.length > 0
                    ? userData.institutionIds
                    : (userData.institutionId ? [userData.institutionId] : []);

                const primarySpecialityId = specialityIds[0] || '';
                const primaryInstitutionId = institutionIds[0] || '';
                // Post-migration preceptors already carry a populated rankIds list;
                // pre-migration ones fall back to resolving the legacy `rank` string
                // once the rank list has loaded (see the effect below).
                const rankIds = Array.isArray(userData.rankIds) ? userData.rankIds.map(String) : [];

                // Set preceptor-specific fields
                setUserData({
                    ...baseData,
                    rank: userData.rank || '',
                    rankIds,
                    specialityId: primarySpecialityId,
                    institutionId: primaryInstitutionId,
                    specialityIds,
                    institutionIds,
                    subSpeciality: userData.subSpeciality || '',
                    subInstitution: userData.subInstitution || '',
                    isAdmin: userData.isAdmin || false,
                });
            } else {
                // For admins, only set basic fields
                setUserData({
                    ...baseData,
                    rank: '',
                    studyType: '',
                    rankIds: [],
                    specialityId: '',
                    institutionId: '',
                    specialityIds: [],
                    institutionIds: [],
                    subSpeciality: '',
                    subInstitution: '',
                    isAdmin: userData.isAdmin || false,
                });
            }
            
            // Update sub-specialities and sub-institutions only for preceptors
            if (detectedUserType === 'preceptor') {
                const specialityIds = Array.isArray(userData.specialityIds) && userData.specialityIds.length > 0
                    ? userData.specialityIds
                    : (userData.specialityId ? [userData.specialityId] : []);
                const institutionIds = Array.isArray(userData.institutionIds) && userData.institutionIds.length > 0
                    ? userData.institutionIds
                    : (userData.institutionId ? [userData.institutionId] : []);
                updateSubSpecialities(specialityIds[0] || '');
                updateSubInstitutions(institutionIds[0] || '');
            }

            setFieldErrors({ id_number: '', id_expiry_date: '', iban: '' });
            setError('');
        } catch (error) {
            console.error('Error fetching user data:', error);
            setError('Failed to load user data: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!validateNewFields()) {
            setError('Please fix the validation errors before saving.');
            return;
        }
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            const token = localStorage.getItem('access_token');
            
            // Determine the user type from the data or use the passed userType
            let detectedUserType = userType;
            
            // If userType is not provided, try to detect it from the data
            if (!detectedUserType) {
                if (userData.rank && ['spep', 'pharmd', 'alumni'].includes(userData.rank.toLowerCase())) {
                    detectedUserType = 'student';
                } else if (
                    (Array.isArray(userData.specialityIds) && userData.specialityIds.length > 0) ||
                    (Array.isArray(userData.institutionIds) && userData.institutionIds.length > 0) ||
                    userData.specialityId ||
                    userData.institutionId
                ) {
                    detectedUserType = 'preceptor';
                } else if (userData.isAdmin) {
                    detectedUserType = 'admin';
                } else {
                    detectedUserType = 'admin'; // Default fallback
                }
            }
            
            // Handle preceptor-admin case - treat as preceptor with admin privileges
            if (detectedUserType === 'preceptor-admin') {
                detectedUserType = 'preceptor';
            }
            
            // Determine the correct endpoint and payload based on detected userType
            let endpoint = '';
            let payload = {};
            
            switch (detectedUserType) {
                case 'student':
                    endpoint = getApiUrl(`crud/modify_student_data`);
                    payload = {
                        _id: userId,
                        full_name: userData.full_name?.trim() || `${userData.firstname} ${userData.lastname}`.trim(),
                        firstname: userData.firstname,
                        lastname: userData.lastname,
                        email: userData.email,
                        phone: userData.phone,
                        address: userData.address,
                        rank: userData.rank,
                        studyType: userData.studyType,
                        id_number: userData.id_number?.trim() || undefined,
                        id_expiry_date: userData.id_expiry_date?.trim() || undefined,
                        iban: userData.iban?.trim() || undefined,
                        nationality: userData.nationality?.trim() || undefined,
                    };
                    break;
                case 'preceptor':
                    endpoint = getApiUrl(`crud/modify_preceptor_data`);
                    const specialityIds = Array.isArray(userData.specialityIds) ? userData.specialityIds.filter(Boolean) : [];
                    const institutionIds = Array.isArray(userData.institutionIds) ? userData.institutionIds.filter(Boolean) : [];
                    const primarySpecialityId = specialityIds[0] || userData.specialityId || '';
                    const primaryInstitutionId = institutionIds[0] || userData.institutionId || '';
                    payload = {
                        _id: userId,
                        full_name: userData.full_name?.trim() || `${userData.firstname} ${userData.lastname}`.trim(),
                        firstname: userData.firstname,
                        lastname: userData.lastname,
                        email: userData.email,
                        phone: userData.phone,
                        address: userData.address,
                        rankIds: Array.isArray(userData.rankIds) ? userData.rankIds.filter(Boolean) : [],
                        specialityId: primarySpecialityId,
                        specialityIds,
                        DisplaySpeciality: primarySpecialityId ? { name: (specialities.find(s => s._id === primarySpecialityId)?.name ?? '') } : null,
                        institutionId: primaryInstitutionId,
                        institutionIds,
                        subSpeciality: userData.subSpeciality || '',
                        subInstitution: userData.subInstitution || '',
                        id_number: userData.id_number?.trim() || undefined,
                        id_expiry_date: userData.id_expiry_date?.trim() || undefined,
                        iban: userData.iban?.trim() || undefined,
                        nationality: userData.nationality?.trim() || undefined,
                        ...(canEditAdminPrivileges ? { isAdmin: userData.isAdmin || false } : {}),
                    };
                    break;
                case 'admin':
                case 'standalone-admin':
                case 'super_admin':
                    // Note: 'preceptor-admin' never reaches here — it's normalized to
                    // 'preceptor' above, since a preceptor who is also an admin still
                    // lives in db.preceptors, not db.admins.
                    endpoint = getApiUrl(`crud/modify_admin_data`);
                    payload = {
                        _id: userId,
                        full_name: userData.full_name?.trim() || `${userData.firstname} ${userData.lastname}`.trim(),
                        firstname: userData.firstname,
                        lastname: userData.lastname,
                        email: userData.email,
                        phone: userData.phone,
                        address: userData.address,
                        id_number: userData.id_number?.trim() || undefined,
                        id_expiry_date: userData.id_expiry_date?.trim() || undefined,
                        iban: userData.iban?.trim() || undefined,
                        nationality: userData.nationality?.trim() || undefined,
                    };
                    break;
                default:
                    throw new Error(`Unable to determine user type for modification. Detected type: ${detectedUserType}`);
            }



            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('Sending payload:', payload);
            const response = await fetch(endpoint, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to update user data: ${errorText}`);
            }

            setSuccess('Profile updated successfully!');
            setTimeout(() => setSuccess(''), 3000);
        } catch (error) {
            console.error('Error updating user data:', error);
            setError('Failed to update profile: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    // Validation: ID = exactly 11 digits; ID Expiry = strict dd/mm/yy and not expired; IBAN = up to 34 alphanumeric
    const validateIdNumber = (val) => {
        const v = (val ?? '').toString().trim();
        if (!v) return '';
        return /^\d{11}$/.test(v) ? '' : 'Must be exactly 11 digits';
    };

    const normalizeIdExpiryDateInput = (val) => {
        const digitsOnly = (val ?? '').toString().replace(/\D/g, '').slice(0, 6);
        if (digitsOnly.length <= 2) return digitsOnly;
        if (digitsOnly.length <= 4) return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
        return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4)}`;
    };

    const parseIdExpiryDate = (val) => {
        const v = (val ?? '').toString().trim();
        if (!/^\d{2}\/\d{2}\/\d{2}$/.test(v)) return null;

        const [dStr, mStr, yStr] = v.split('/');
        const d = Number(dStr);
        const m = Number(mStr);
        const y = Number(yStr);

        // API/UI convention uses dd/mm/yy for contemporary dates.
        const fullYear = 2000 + y;
        const date = new Date(fullYear, m - 1, d);

        if (
            Number.isNaN(date.getTime()) ||
            date.getFullYear() !== fullYear ||
            date.getMonth() !== m - 1 ||
            date.getDate() !== d
        ) {
            return null;
        }

        return date;
    };

    const validateIdExpiryDate = (val) => {
        const v = (val ?? '').toString().trim();
        if (!v) return '';
        if (!/^\d{2}\/\d{2}\/\d{2}$/.test(v)) return 'Use format dd/mm/yy (e.g. 31/12/25)';

        const parsedDate = parseIdExpiryDate(v);
        if (!parsedDate) return 'Invalid date';

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (parsedDate < today) return 'ID expiry date cannot be in the past';

        return '';
    };
    const validateIban = (val) => {
        const v = (val ?? '').toString().trim();
        if (!v) return '';
        if (v.length > 34) return 'Max 34 characters';
        return /^[A-Za-z0-9]+$/.test(v) ? '' : 'Only letters and numbers allowed (max 34)';
    };

    const validateNewFields = () => {
        const errs = {
            id_number: validateIdNumber(userData.id_number),
            id_expiry_date: validateIdExpiryDate(userData.id_expiry_date),
            iban: validateIban(userData.iban),
        };
        setFieldErrors(errs);
        return !errs.id_number && !errs.id_expiry_date && !errs.iban;
    };

    const handleInputChange = (field, value) => {
        if (field === 'firstname' || field === 'lastname') {
            setUserData(prev => {
                const next = { ...prev, [field]: value };
                next.full_name = `${next.firstname} ${next.lastname}`.trim();
                return next;
            });
        } else {
            setUserData(prev => ({ ...prev, [field]: value }));
            if (['id_number', 'id_expiry_date', 'iban'].includes(field) && fieldErrors[field]) {
                setFieldErrors(prev => ({ ...prev, [field]: '' }));
            }
        }
    };

    const handleBlurNewField = (field) => {
        const fns = { id_number: validateIdNumber, id_expiry_date: validateIdExpiryDate, iban: validateIban };
        const msg = fns[field](userData[field]);
        setFieldErrors(prev => ({ ...prev, [field]: msg }));
    };

    const getCurrentSpecialityName = () => {
        const ids = Array.isArray(userData.specialityIds) ? userData.specialityIds : [];
        if (ids.length === 0) return userData.specialityId ? 'N/A' : 'N/A';
        const names = ids.map((id) => (specialities.find(s => s._id === id)?.name) || id).filter(Boolean);
        return names.length ? names.join(', ') : 'N/A';
    };

    const getCurrentInstitutionName = () => {
        const ids = Array.isArray(userData.institutionIds) ? userData.institutionIds : [];
        if (ids.length === 0) return userData.institutionId ? 'N/A' : 'N/A';
        const names = ids.map((id) => (institutions.find(i => i._id === id)?.name) || id).filter(Boolean);
        return names.length ? names.join(', ') : 'N/A';
    };

    const updateSubSpecialities = (specialityId) => {
        if (!specialityId) {
            setSubSpecialities([]);
            return;
        }
        const speciality = specialities.find(s => s._id === specialityId);
        setSubSpecialities(Array.isArray(speciality?.subSpecialities) ? speciality.subSpecialities : []);
    };

    const updateSubInstitutions = (institutionId) => {
        if (!institutionId) {
            setSubInstitutions([]);
            return;
        }
        const institution = institutions.find(i => i._id === institutionId);
        setSubInstitutions(Array.isArray(institution?.subInstitutions) ? institution.subInstitutions : []);
    };

    const handleSpecialityChange = (specialityId) => {
        setUserData(prev => ({
            ...prev,
            specialityId,
            subSpeciality: '' // Reset Sub-Rotation/Sub-Experience when main Rotation/Experience changes
        }));
        updateSubSpecialities(specialityId);
    };

    const handleInstitutionChange = (institutionId) => {
        setUserData(prev => ({
            ...prev,
            institutionId,
            subInstitution: '' // Reset sub-institution when main institution changes
        }));
        updateSubInstitutions(institutionId);
    };

    const handleSpecialityIdsChange = (specialityIds) => {
        const ids = Array.isArray(specialityIds) ? specialityIds : [];
        const primary = ids[0] || '';
        setUserData(prev => ({
            ...prev,
            specialityIds: ids,
            specialityId: primary,
            subSpeciality: '' // primary changed potentially
        }));
        updateSubSpecialities(primary);
    };

    const handleInstitutionIdsChange = (institutionIds) => {
        const ids = Array.isArray(institutionIds) ? institutionIds : [];
        const primary = ids[0] || '';
        setUserData(prev => ({
            ...prev,
            institutionIds: ids,
            institutionId: primary,
            subInstitution: '' // primary changed potentially
        }));
        updateSubInstitutions(primary);
    };

    if (loading) {
        return (
            <div className="modify-user-page-shell modify-user-loading-shell">
                <LoadingSpinner text="Loading profile..." />
            </div>
        );
    }

    return (
        <div className="modify-user-page-shell">
            {/* Header Card */}
            <div className="modify-user-theme-header-card">
                {/* Header gradient bar */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                    borderRadius: '12px 12px 0 0'
                }}></div>
                
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '0.5rem'
                }}>
                    <div style={{
                        width: '36px',
                        height: '36px',
                        background: 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.1rem',
                        boxShadow: '0 2px 8px rgba(43, 70, 99, 0.2)'
                    }}>
                        <FaUserEdit />
                    </div>
                    <div>
                        <h1 style={{
                            margin: 0,
                            fontSize: '1.25rem',
                            fontWeight: 700,
                            color: '#1e293b',
                            letterSpacing: '-0.025em'
                        }}>
                            Edit User Profile
                        </h1>
                        <p style={{
                            margin: 0,
                            color: '#64748b',
                            fontSize: '0.85rem',
                            fontWeight: 500
                        }}>
                            Update user information and account settings
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Form Card */}
            <div
                className="modify-user-content-grid"
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 460px)',
                    gap: '1.5rem',
                    alignItems: 'start'
                }}
            >
                <div className="modify-user-main-column" style={{ minWidth: 0 }}>
                    <div className="modify-user-theme-form-card" style={{
                        padding: '1.25rem',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                {/* Subtle background pattern */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '120px',
                    height: '120px',
                    background: 'radial-gradient(circle, rgba(43, 70, 99, 0.02) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }}></div>

                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                    gap: '2rem',
                    position: 'relative',
                    zIndex: 1
                }}>
                    {/* First Name (editable) */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <FaUser style={{ color: '#4a90e2', fontSize: '0.9rem' }} />
                                First Name
                            </div>
                        </label>
                        <input
                            type="text"
                            value={userData.firstname}
                            onChange={(e) => handleInputChange('firstname', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                boxSizing: 'border-box'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4a90e2';
                                e.target.style.background = 'white';
                                e.target.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)';
                                e.target.style.transform = 'translateY(-1px)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.background = 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)';
                                e.target.style.boxShadow = 'none';
                                e.target.style.transform = 'translateY(0)';
                            }}
                            placeholder="Enter first name"
                        />
                    </div>

                    {/* Last Name (editable) */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <FaUser style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                Last Name
                            </div>
                        </label>
                        <input
                            type="text"
                            value={userData.lastname}
                            onChange={(e) => handleInputChange('lastname', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                boxSizing: 'border-box'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4a90e2';
                                e.target.style.background = 'white';
                                e.target.style.boxShadow = '0 0 0 3px rgba(74, 144, 226, 0.1)';
                                e.target.style.transform = 'translateY(-1px)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.background = 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)';
                                e.target.style.boxShadow = 'none';
                                e.target.style.transform = 'translateY(0)';
                            }}
                            placeholder="Enter last name"
                        />
                    </div>

                    {/* Full Name (read-only; derived from First + Last name) */}
                    <div style={{ gridColumn: '1 / -1' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <FaUser style={{ color: '#4a90e2', fontSize: '0.9rem' }} />
                                Full Name
                            </div>
                        </label>
                        <input
                            type="text"
                            value={userData.full_name}
                            readOnly
                            disabled
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                background: '#f1f5f9',
                                color: '#64748b',
                                cursor: 'not-allowed',
                                boxSizing: 'border-box'
                            }}
                            placeholder="First name + Last name"
                        />
                    </div>

                    {/* Email */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <FaEnvelope style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                Email Address
                            </div>
                        </label>
                        <input
                            type="email"
                            value={userData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '1rem 1.25rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '12px',
                                fontSize: '1rem',
                                fontWeight: 500,
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                boxSizing: 'border-box'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4a90e2';
                                e.target.style.background = 'white';
                                e.target.style.boxShadow = '0 0 0 4px rgba(74, 144, 226, 0.1)';
                                e.target.style.transform = 'translateY(-2px)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.background = 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)';
                                e.target.style.boxShadow = 'none';
                                e.target.style.transform = 'translateY(0)';
                            }}
                            placeholder="Enter email address"
                        />
                    </div>

                    {/* Phone */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <FaPhone style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                Phone Number
                            </div>
                        </label>
                        <input
                            type="tel"
                            value={userData.phone}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '1rem 1.25rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '12px',
                                fontSize: '1rem',
                                fontWeight: 500,
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                boxSizing: 'border-box'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4a90e2';
                                e.target.style.background = 'white';
                                e.target.style.boxShadow = '0 0 0 4px rgba(74, 144, 226, 0.1)';
                                e.target.style.transform = 'translateY(-2px)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.background = 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)';
                                e.target.style.boxShadow = 'none';
                                e.target.style.transform = 'translateY(0)';
                            }}
                            placeholder="Enter phone number"
                        />
                    </div>

                    {/* Address */}
                    <div style={{ 
                        gridColumn: '1 / -1',
                        marginBottom: '0.5rem'
                    }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                            }}>
                                <FaMapMarkerAlt style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                Address
                            </div>
                        </label>
                        <textarea
                            value={userData.address}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            rows={3}
                            style={{
                                width: '100%',
                                padding: '1rem 1.25rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '12px',
                                fontSize: '1rem',
                                fontWeight: 500,
                                outline: 'none',
                                transition: 'all 0.3s ease',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                boxSizing: 'border-box',
                                resize: 'vertical'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4a90e2';
                                e.target.style.background = 'white';
                                e.target.style.boxShadow = '0 0 0 4px rgba(74, 144, 226, 0.1)';
                                e.target.style.transform = 'translateY(-2px)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#e2e8f0';
                                e.target.style.background = 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)';
                                e.target.style.boxShadow = 'none';
                                e.target.style.transform = 'translateY(0)';
                            }}
                            placeholder="Enter address"
                        />
                    </div>

                    {/* QID number */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            QID number
                        </label>
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={11}
                            value={userData.id_number}
                            onChange={(e) => {
                                const v = e.target.value.replace(/\D/g, '');
                                handleInputChange('id_number', v);
                            }}
                            onBlur={() => handleBlurNewField('id_number')}
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                border: `2px solid ${fieldErrors.id_number ? '#dc2626' : '#e2e8f0'}`,
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                outline: 'none',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                boxSizing: 'border-box'
                            }}
                            placeholder="11 digits"
                        />
                        {fieldErrors.id_number && (
                            <span style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.id_number}</span>
                        )}
                    </div>

                    {/* QID Expiry Date (dd/mm/yy) */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            QID Expiry Date (dd/mm/yy)
                        </label>
                        <input
                            type="text"
                            value={userData.id_expiry_date}
                            inputMode="numeric"
                            maxLength={8}
                            onChange={(e) => handleInputChange('id_expiry_date', normalizeIdExpiryDateInput(e.target.value))}
                            onBlur={() => handleBlurNewField('id_expiry_date')}
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                border: `2px solid ${fieldErrors.id_expiry_date ? '#dc2626' : '#e2e8f0'}`,
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                outline: 'none',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                boxSizing: 'border-box'
                            }}
                            pattern="\\d{2}/\\d{2}/\\d{2}"
                            placeholder="e.g. 31/12/25"
                        />
                        {fieldErrors.id_expiry_date && (
                            <span style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.id_expiry_date}</span>
                        )}
                    </div>

                    {/* IBAN number (up to 34 alphanumeric) */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            IBAN number (up to 34 characters)
                        </label>
                        <input
                            type="text"
                            maxLength={34}
                            value={userData.iban}
                            onChange={(e) => {
                                const v = e.target.value.replace(/[^A-Za-z0-9]/g, '');
                                handleInputChange('iban', v);
                            }}
                            onBlur={() => handleBlurNewField('iban')}
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                border: `2px solid ${fieldErrors.iban ? '#dc2626' : '#e2e8f0'}`,
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                outline: 'none',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                boxSizing: 'border-box'
                            }}
                            placeholder="Letters and numbers only, max 34"
                        />
                        {fieldErrors.iban && (
                            <span style={{ fontSize: '0.8rem', color: '#dc2626', marginTop: '0.25rem', display: 'block' }}>{fieldErrors.iban}</span>
                        )}
                    </div>

                    {/* Nationality */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.75rem',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            color: '#374151',
                            letterSpacing: '0.025em'
                        }}>
                            Nationality
                        </label>
                        <input
                            type="text"
                            value={userData.nationality}
                            onChange={(e) => handleInputChange('nationality', e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.875rem 1rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                outline: 'none',
                                background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                boxSizing: 'border-box'
                            }}
                            placeholder="e.g. Qatar"
                        />
                    </div>

                    {/* User Type Specific Fields */}
                    {/* Student specific fields - Only show if detected user type is 'student' */}
                    {getDetectedUserType() === 'student' && (
                        <>
                            {/* Rank */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.75rem',
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    color: '#374151',
                                    letterSpacing: '0.025em'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FaGraduationCap style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                        Rank
                                    </div>
                                </label>
                                <RankSelect
                                    value={userData.rank}
                                    onChange={(e) => handleInputChange('rank', e.target.value)}
                                />
                            </div>

                            {/* Study Type */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.75rem',
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    color: '#374151',
                                    letterSpacing: '0.025em'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FaGraduationCap style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                        Study Type
                                    </div>
                                </label>
                                <select
                                    value={userData.studyType}
                                    onChange={(e) => handleInputChange('studyType', e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '1rem 1.25rem',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: '12px',
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        background: 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                        boxSizing: 'border-box',
                                        cursor: 'pointer'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#4a90e2';
                                        e.target.style.background = 'white';
                                        e.target.style.boxShadow = '0 0 0 4px rgba(74, 144, 226, 0.1)';
                                        e.target.style.transform = 'translateY(-2px)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#e2e8f0';
                                        e.target.style.background = 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)';
                                        e.target.style.boxShadow = 'none';
                                        e.target.style.transform = 'translateY(0)';
                                    }}
                                >
                                    <option value="">Select study type</option>
                                    <option value="full-time">Full-time</option>
                                    <option value="part-time">Part-time</option>
                                </select>
                            </div>
                        </>
                    )}

                    {/* Preceptor specific fields - Only show if detected user type is 'preceptor' */}
                    {getDetectedUserType() === 'preceptor' && (
                        <>
                            {/* Rank for Preceptors */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.75rem',
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    color: '#374151',
                                    letterSpacing: '0.025em'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FaUserMd style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                        Rank(s) <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.85em', marginLeft: '0.4rem' }}>(select one or more)</span>
                                    </div>
                                </label>
                                <RankMultiSelect
                                    value={userData.rankIds}
                                    onChange={(rankIds) => setUserData((prev) => ({ ...prev, rankIds }))}
                                    ranks={ranks}
                                    ranksLoading={false}
                                />
                            </div>

                            {/* Rotation/Experience - Main Field */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.75rem',
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    color: '#374151',
                                    letterSpacing: '0.025em'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FaUserMd style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                        Rotation/Experience
                                    </div>
                                </label>
                                {/* Current speciality display */}
                                <div style={{
                                    marginBottom: '0.5rem',
                                    padding: '0.5rem',
                                    background: '#f8fafc',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    color: '#64748b'
                                }}>
                                    Current: <strong>{getCurrentSpecialityName()}</strong>
                                </div>
                                <SearchableDropdown
                                    options={specialities}
                                    multiple
                                    selectedValues={userData.specialityIds}
                                    onSelectionChange={handleSpecialityIdsChange}
                                    placeholder="Select Rotation/Experience..."
                                    optionLabel="name"
                                    optionValue="_id"
                                    loading={loading}
                                    size="medium"
                                />
                            </div>

                            {/* Sub-Speciality - Sub Field */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.75rem',
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    color: '#374151',
                                    letterSpacing: '0.025em'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FaUserMd style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                        Sub-Rotation/Sub-Experience
                                    </div>
                                </label>
                                {/* Current sub-speciality display */}
                                <div style={{
                                    marginBottom: '0.5rem',
                                    padding: '0.5rem',
                                    background: '#f8fafc',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    color: '#64748b'
                                }}>
                                    Current: <strong>{userData.subSpeciality || 'N/A'}</strong>
                                </div>
                                <select
                                    value={userData.subSpeciality}
                                    onChange={(e) => handleInputChange('subSpeciality', e.target.value)}
                                    disabled={!userData.specialityId || subSpecialities.length === 0}
                                    style={{
                                        width: '100%',
                                        padding: '1rem 1.25rem',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: '12px',
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        background: (!userData.specialityId || subSpecialities.length === 0) 
                                            ? 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)' 
                                            : 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                        boxSizing: 'border-box',
                                        cursor: (!userData.specialityId || subSpecialities.length === 0) ? 'not-allowed' : 'pointer',
                                        opacity: (!userData.specialityId || subSpecialities.length === 0) ? 0.6 : 1
                                    }}
                                    onFocus={(e) => {
                                        if (userData.specialityId && subSpecialities.length > 0) {
                                            e.target.style.borderColor = '#4a90e2';
                                            e.target.style.background = 'white';
                                            e.target.style.boxShadow = '0 0 0 4px rgba(74, 144, 226, 0.1)';
                                            e.target.style.transform = 'translateY(-2px)';
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (userData.specialityId && subSpecialities.length > 0) {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.background = 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)';
                                            e.target.style.boxShadow = 'none';
                                            e.target.style.transform = 'translateY(0)';
                                        }
                                    }}
                                >
                                    <option value="">N/A</option>
                                    {subSpecialities.map(subSpec => (
                                        <option key={subSpec} value={subSpec}>
                                            {subSpec}
                                        </option>
                                    ))}
                                </select>
                                {(!userData.specialityId || subSpecialities.length === 0) && (
                                    <p style={{
                                        margin: '0.5rem 0 0 0',
                                        fontSize: '0.8rem',
                                        color: '#9ca3af',
                                        fontStyle: 'italic'
                                    }}>
                                        {!userData.specialityId 
                                            ? 'Select a Rotation/Experience first' 
                                            : 'No Sub-Rotation/Sub-Experience available for selected Rotation/Experience'
                                        }
                                    </p>
                                )}
                            </div>

                            {/* Institution */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.75rem',
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    color: '#374151',
                                    letterSpacing: '0.025em'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FaHospital style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                        Institution
                                    </div>
                                </label>
                                {/* Current institution display */}
                                <div style={{
                                    marginBottom: '0.5rem',
                                    padding: '0.5rem',
                                    background: '#f8fafc',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    color: '#64748b'
                                }}>
                                    Current: <strong>{getCurrentInstitutionName()}</strong>
                                </div>
                                <SearchableDropdown
                                    options={institutions}
                                    multiple
                                    selectedValues={userData.institutionIds}
                                    onSelectionChange={handleInstitutionIdsChange}
                                    placeholder="Select institution..."
                                    optionLabel="name"
                                    optionValue="_id"
                                    loading={loading}
                                    size="medium"
                                />
                            </div>

                            {/* Sub-Institution */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.75rem',
                                    fontSize: '0.95rem',
                                    fontWeight: 700,
                                    color: '#374151',
                                    letterSpacing: '0.025em'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <FaHospital style={{ color: '#4a90e2', fontSize: '1rem' }} />
                                        Sub-Institution
                                    </div>
                                </label>
                                {/* Current sub-institution display */}
                                <div style={{
                                    marginBottom: '0.5rem',
                                    padding: '0.5rem',
                                    background: '#f8fafc',
                                    borderRadius: '8px',
                                    fontSize: '0.875rem',
                                    color: '#64748b'
                                }}>
                                    Current: <strong>{userData.subInstitution || 'N/A'}</strong>
                                </div>
                                <select
                                    value={userData.subInstitution}
                                    onChange={(e) => handleInputChange('subInstitution', e.target.value)}
                                    disabled={!userData.institutionId || subInstitutions.length === 0}
                                    style={{
                                        width: '100%',
                                        padding: '1rem 1.25rem',
                                        border: '2px solid #e2e8f0',
                                        borderRadius: '12px',
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        outline: 'none',
                                        transition: 'all 0.3s ease',
                                        background: (!userData.institutionId || subInstitutions.length === 0) 
                                            ? 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)' 
                                            : 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)',
                                        boxSizing: 'border-box',
                                        cursor: (!userData.institutionId || subInstitutions.length === 0) ? 'not-allowed' : 'pointer',
                                        opacity: (!userData.institutionId || subInstitutions.length === 0) ? 0.6 : 1
                                    }}
                                    onFocus={(e) => {
                                        if (userData.institutionId && subInstitutions.length > 0) {
                                            e.target.style.borderColor = '#4a90e2';
                                            e.target.style.background = 'white';
                                            e.target.style.boxShadow = '0 0 0 4px rgba(74, 144, 226, 0.1)';
                                            e.target.style.transform = 'translateY(-2px)';
                                        }
                                    }}
                                    onBlur={(e) => {
                                        if (userData.institutionId && subInstitutions.length > 0) {
                                            e.target.style.borderColor = '#e2e8f0';
                                            e.target.style.background = 'linear-gradient(135deg, #fafafa 0%, #f8fafc 100%)';
                                            e.target.style.boxShadow = 'none';
                                            e.target.style.transform = 'translateY(0)';
                                        }
                                    }}
                                >
                                    <option value="">N/A</option>
                                    {subInstitutions.map(subInst => (
                                        <option key={subInst} value={subInst}>
                                            {subInst}
                                        </option>
                                    ))}
                                </select>
                                {(!userData.institutionId || subInstitutions.length === 0) && (
                                    <p style={{
                                        margin: '0.5rem 0 0 0',
                                        fontSize: '0.8rem',
                                        color: '#9ca3af',
                                        fontStyle: 'italic'
                                    }}>
                                        {!userData.institutionId 
                                            ? 'Select an institution first' 
                                            : 'No sub-institutions available for selected institution'
                                        }
                                    </p>
                                )}
                            </div>

                            {canEditAdminPrivileges && (
                                <div style={{
                                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    padding: '1.5rem'
                                }}>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: '#374151',
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }}>
                                        <div style={{
                                            position: 'relative',
                                            display: 'inline-block'
                                        }}>
                                            <input
                                                type="checkbox"
                                                checked={userData.isAdmin}
                                                onChange={(e) => handleInputChange('isAdmin', e.target.checked)}
                                                style={{
                                                    width: '1.5rem',
                                                    height: '1.5rem',
                                                    cursor: 'pointer',
                                                    accentColor: '#4a90e2'
                                                }}
                                            />
                                        </div>
                                        <span>Administrator Privileges</span>
                                    </label>
                                    <p style={{
                                        margin: '0.5rem 0 0 2.5rem',
                                        fontSize: '0.875rem',
                                        color: '#64748b',
                                        fontWeight: 500
                                    }}>
                                        {userData.isAdmin 
                                            ? 'This preceptor has administrator privileges' 
                                            : 'This preceptor does not have administrator privileges'
                                        }
                                    </p>
                                </div>
                            )}
                            {isPreceptorSelfAccount && userData.isAdmin && (
                                <div style={{
                                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '12px',
                                    padding: '1rem 1.25rem'
                                }}>
                                    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#1d4ed8', marginBottom: '0.25rem' }}>
                                        Administrator Privileges
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: '#1e40af', fontWeight: 500 }}>
                                        This account has administrator access and it is managed by an admin.
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* Save Button */}
                    <div style={{ 
                        gridColumn: '1 / -1',
                        paddingTop: '1rem',
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                background: saving 
                                    ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)' 
                                    : 'linear-gradient(135deg, #2b4663 0%, #4a90e2 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '0.75rem 1.75rem',
                                color: 'white',
                                fontSize: '0.85rem',
                                fontWeight: 700,
                                cursor: saving ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                transition: 'all 0.3s ease',
                                boxShadow: saving 
                                    ? '0 2px 8px rgba(0, 0, 0, 0.1)' 
                                    : '0 4px 16px rgba(43, 70, 99, 0.2)',
                                letterSpacing: '0.025em',
                                textTransform: 'uppercase'
                            }}
                            onMouseEnter={(e) => {
                                if (!saving) {
                                    e.target.style.transform = 'translateY(-1px) scale(1.01)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(43, 70, 99, 0.25)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!saving) {
                                    e.target.style.transform = 'translateY(0) scale(1)';
                                    e.target.style.boxShadow = '0 4px 16px rgba(43, 70, 99, 0.2)';
                                }
                            }}
                        >
                            <FaSave style={{ fontSize: '0.9rem' }} />
                            {saving ? 'Saving Changes...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
                </div>
                </div>
                <div className="modify-user-documents-column" style={{ minWidth: 0 }}>
                    <div className="modify-user-theme-form-card modify-user-documents-card" style={{ padding: '1.25rem' }}>
                        <UserDocuments userId={userId} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModifyUser;
